import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/synapse/Icon';
import { Segmented } from '@/components/synapse/Segmented';
import { StatusDot } from '@/components/synapse/StatusDot';
import { DatePicker } from '@/components/synapse/DatePicker';
import { Select } from '@/components/synapse/Select';
import { Button } from '@/components/ui/button';
import { useLabLaboratories, useLabWorklist } from '@/features/lab/useLab';
import {
  defaultRange,
  facetByStatus,
  liveSpecimenTypes,
  liveTestNames,
  WORKLIST_SEGMENTS,
  type WorklistSegment,
} from '@/features/lab/labWorklist';
import { orderStatusTone, translateOrderStatus, translateSpecimenType } from '@/lib/labTests';
import { useCan } from '@/hooks/usePermissions';
import { formatRelativeTime } from '@/lib/relativeTime';
import type { LabWorklistRow } from '@/types/lab';
import type { SpecimenType } from '@synapse/lab-tests';

/**
 * The lab desk's primary surface: what has been ordered, what still needs a
 * sample, and what is waiting on a report.
 *
 * The window is a DATE RANGE (default: today) and the status segments are facets
 * over the rows in it — not server-side filters. An order's status is derived
 * from its tests and specimens, so there is no column to filter or count on in
 * SQL; faceting client-side over a day's orders keeps the counts honest instead
 * of showing a number that means "on this page". When the server's cap is hit we
 * say so rather than let a truncated list read as complete.
 */

// Matches the server's clamp. Named here so the cap notice and the request
// cannot disagree about what "full" means.
const PAGE_LIMIT = 100;

const SEGMENT_OPTIONS = WORKLIST_SEGMENTS.map((value) => ({
  value,
  labelKey: `lab.segment.${value}`,
}));

const TONE_TO_DOT = {
  success: 'ok',
  info: 'warn',
  danger: 'danger',
  neutral: 'neutral',
  muted: 'neutral',
} as const;

export function LabWorklistPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const can = useCan();

  // The unfiltered day. Kept in step with WORKLIST_SEGMENTS[0] so the highlighted
  // tab is always the leading one — see the comment on that constant.
  const [segment, setSegment] = useState<WorklistSegment>('all');
  const [range, setRange] = useState(() => defaultRange());
  const [query, setQuery] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);
  // '' = any, 'none' = not routed anywhere yet, otherwise a laboratory id.
  const [laboratoryId, setLaboratoryId] = useState('');

  // `includeInactive` here, unlike the ordering picker: filtering by a lab the
  // clinic has since retired is a legitimate question about past work.
  const laboratories = useLabLaboratories({ includeInactive: true });

  const worklist = useLabWorklist({
    from: range.from,
    to: range.to,
    q: query.trim() || undefined,
    // Its own filter rather than a term folded into `q`: the search box's arms
    // are index-backed by design, and this one ANDs instead of ORing.
    laboratoryId: laboratoryId || undefined,
    includeCancelled: includeCancelled || undefined,
    limit: PAGE_LIMIT,
  });

  const rows = useMemo(() => worklist.data ?? [], [worklist.data]);
  const facets = useMemo(() => facetByStatus(rows), [rows]);
  const visible = facets[segment];
  const capped = rows.length >= PAGE_LIMIT;

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 py-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-[-0.02em] text-ink">{t('lab.title')}</h1>
          <p className="text-[13px] text-muted">{t('lab.subtitle')}</p>
        </div>
        {can('lab.orders.create') && (
          <Button onClick={() => navigate('/lab/new')} className="gap-1.5">
            <Icon name="plus" size={15} />
            {t('lab.newOrder')}
          </Button>
        )}
      </header>

      <div className="flex flex-col gap-3">
        <Segmented
          value={segment}
          onChange={setSegment}
          options={SEGMENT_OPTIONS}
          ariaLabel={t('lab.filterLabel')}
        />

        <div className="flex flex-wrap items-end gap-3">
          <label className="flex min-w-[220px] flex-1 flex-col gap-1">
            <span className="text-[12px] font-medium text-muted">{t('lab.searchLabel')}</span>
            <span className="flex h-11 items-center gap-2 rounded-md border border-line-2 bg-paper px-3">
              <Icon name="search" size={15} className="shrink-0 text-muted" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={t('lab.searchLabel')}
                placeholder={t('lab.searchPlaceholder')}
                className="h-full flex-1 bg-transparent text-[13.5px] text-ink outline-none placeholder:text-muted-2"
              />
            </span>
          </label>

          <label className="flex flex-col gap-1" htmlFor="lab-laboratory">
            <span className="text-[12px] font-medium text-muted">{t('lab.laboratory')}</span>
            <Select
              id="lab-laboratory"
              value={laboratoryId}
              onChange={(e) => setLaboratoryId(e.target.value)}
              aria-label={t('lab.filterLaboratory')}
              className="w-52"
            >
              <option value="">{t('lab.allLaboratories')}</option>
              <option value="none">{t('lab.noLaboratory')}</option>
              {(laboratories.data ?? []).map((lab) => (
                <option key={lab.id} value={lab.id}>
                  {lab.active ? lab.name : `${lab.name} — ${t('lab.laboratories.hidden')}`}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1" htmlFor="lab-from">
            <span className="text-[12px] font-medium text-muted">{t('lab.dateFrom')}</span>
            <DatePicker
              id="lab-from"
              value={range.from}
              max={range.to}
              onChange={(from) => setRange((r) => ({ ...r, from: from ?? r.from }))}
            />
          </label>
          <label className="flex flex-col gap-1" htmlFor="lab-to">
            <span className="text-[12px] font-medium text-muted">{t('lab.dateTo')}</span>
            <DatePicker
              id="lab-to"
              value={range.to}
              min={range.from}
              onChange={(to) => setRange((r) => ({ ...r, to: to ?? r.to }))}
            />
          </label>

          <label className="inline-flex h-11 items-center gap-2 text-[13px] text-muted">
            <input
              type="checkbox"
              checked={includeCancelled}
              onChange={(e) => setIncludeCancelled(e.target.checked)}
              className="h-4 w-4"
            />
            {t('lab.showCancelled')}
          </label>
        </div>
      </div>

      {capped && (
        <p className="rounded-md border border-line-2 bg-paper-2 px-3 py-2 text-[12.5px] text-muted">
          {t('lab.capNotice', { count: PAGE_LIMIT })}
        </p>
      )}

      {worklist.isPending && <WorklistSkeleton />}

      {worklist.isError && (
        <div className="rounded-md border border-line-2 bg-paper px-4 py-6 text-center">
          <p className="text-[13.5px] text-ink">{t('lab.loadFailed')}</p>
          <Button variant="secondary" className="mt-3" onClick={() => void worklist.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      )}

      {!worklist.isPending && !worklist.isError && visible.length === 0 && (
        <p className="rounded-md border border-line-2 bg-paper px-4 py-8 text-center text-[13.5px] text-muted">
          {t(
            segment === 'toCollect'
              ? 'lab.emptyToCollect'
              : segment === 'inLab'
                ? 'lab.emptyInLab'
                : segment === 'resulted'
                  ? 'lab.emptyResulted'
                  : 'lab.emptyAll',
          )}
        </p>
      )}

      {visible.length > 0 && (
        <ul className="flex flex-col gap-2">
          {visible.map((row) => (
            <li key={row.id}>
              <WorklistRow row={row} onOpen={() => navigate(`/lab/${row.id}`)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WorklistRow({ row, onOpen }: { row: LabWorklistRow; onOpen: () => void }) {
  const { t } = useTranslation();
  const tests = liveTestNames(row);
  const specimenTypes = liveSpecimenTypes(row) as SpecimenType[];

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t('lab.openOrder', { accession: row.accessionNumber })}
      className="flex min-h-[44px] w-full flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-line-2 bg-paper px-3 py-2.5 text-left hover:bg-paper-2 focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex min-w-[160px] flex-1 flex-col">
        <span className="text-[13.5px] font-semibold tracking-[-0.01em] text-ink">
          {row.patientName}
        </span>
        <span className="text-[12px] text-muted">{row.patientMrn}</span>
      </span>

      <span className="font-mono text-[12.5px] tabular-nums text-muted">
        {row.accessionNumber}
      </span>

      <span className="flex flex-wrap items-center gap-1">
        {specimenTypes.map((type) => (
          <span
            key={type}
            className="rounded-full border border-line-2 px-2 py-0.5 text-[11.5px] text-muted"
          >
            {translateSpecimenType(t, type)}
          </span>
        ))}
      </span>

      <span className="min-w-[120px] flex-1 truncate text-[12.5px] text-muted">
        {tests.join(', ')}
      </span>

      {/* Shown so the laboratory filter's effect is legible on the rows it returns. */}
      {row.laboratoryName && (
        <span className="max-w-[140px] truncate text-[12px] text-muted-2">
          {row.laboratoryName}
        </span>
      )}

      {row.priority === 'urgent' && (
        <span className="rounded-full bg-destructive px-2 py-0.5 text-[11px] font-medium text-accent-foreground">
          {t('lab.priority.urgent')}
        </span>
      )}

      <span className="inline-flex items-center gap-1.5 text-[12.5px] text-ink">
        <StatusDot tone={TONE_TO_DOT[orderStatusTone(row.status)]} />
        {translateOrderStatus(t, row.status)}
      </span>

      <span className="text-[12px] tabular-nums text-muted-2">
        {formatRelativeTime(row.createdAt, t)}
      </span>
    </button>
  );
}

function WorklistSkeleton() {
  return (
    <ul className="flex flex-col gap-2" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className="h-[58px] animate-pulse rounded-md border border-line-2 bg-paper-2" />
      ))}
    </ul>
  );
}
